import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Send,
  Globe,
  Smartphone,
  Film,
  Palette,
  ShoppingBag,
  Megaphone,
  FileText,
  Plus,
  X,
  Loader2,
  CheckCircle2,
  Link as LinkIcon,
  Building2,
  User,
  Mail,
  Phone,
  Calendar,
  Coins,
  ArrowLeft,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Project types with icons
const PROJECT_TYPES = [
  { value: 'website', label: 'Site Web', icon: Globe, description: 'Site vitrine, e-commerce, blog...' },
  { value: 'mobile_app', label: 'Application Mobile', icon: Smartphone, description: 'iOS, Android ou hybride' },
  { value: 'video', label: 'Vidéo', icon: Film, description: 'Vidéo promotionnelle, corporate...' },
  { value: 'logo', label: 'Logo / Identité', icon: Palette, description: 'Logo, charte graphique...' },
  { value: 'ecommerce', label: 'E-commerce', icon: ShoppingBag, description: 'Boutique en ligne' },
  { value: 'marketing', label: 'Marketing / Pub', icon: Megaphone, description: 'Campagne pub, réseaux sociaux...' },
  { value: 'other', label: 'Autre', icon: FileText, description: 'Autre type de projet' }
];

// Budget options
const BUDGET_OPTIONS = [
  { value: 'small', label: 'Petit budget (< 1 000€)' },
  { value: 'medium', label: 'Budget moyen (1 000€ - 5 000€)' },
  { value: 'large', label: 'Budget important (5 000€ - 15 000€)' },
  { value: 'enterprise', label: 'Budget entreprise (> 15 000€)' },
  { value: 'custom', label: 'À définir ensemble' }
];

// Deadline options
const DEADLINE_OPTIONS = [
  { value: 'urgent', label: 'Urgent (< 1 semaine)' },
  { value: '2_weeks', label: '2 semaines' },
  { value: '1_month', label: '1 mois' },
  { value: '2_months', label: '2 mois' },
  { value: '3_months', label: '3 mois ou plus' },
  { value: 'flexible', label: 'Flexible / Pas de deadline' }
];

// Dynamic brief fields based on project type
const BRIEF_FIELDS = {
  website: [
    { key: 'pages_count', label: 'Nombre de pages estimé', type: 'select', options: ['1-5', '6-10', '11-20', '20+'] },
    { key: 'cms_preference', label: 'Préférence CMS', type: 'select', options: ['WordPress', 'Webflow', 'Sur mesure', 'Pas de préférence'] },
    { key: 'design_style', label: 'Style souhaité', type: 'select', options: ['Moderne', 'Corporate', 'Minimaliste', 'Créatif', 'Autre'] },
    { key: 'features', label: 'Fonctionnalités souhaitées', type: 'multi', options: ['Formulaire de contact', 'Blog', 'Newsletter', 'Multilingue', 'Espace membre', 'Réservation en ligne'] }
  ],
  mobile_app: [
    { key: 'platforms', label: 'Plateformes', type: 'multi', options: ['iOS', 'Android', 'Les deux'] },
    { key: 'user_auth', label: 'Authentification utilisateur', type: 'checkbox' },
    { key: 'features', label: 'Fonctionnalités clés', type: 'multi', options: ['Notifications push', 'Paiement in-app', 'Géolocalisation', 'Messagerie', 'API externe'] }
  ],
  video: [
    { key: 'duration', label: 'Durée souhaitée', type: 'select', options: ['< 30 secondes', '30s - 1 min', '1 - 3 min', '> 3 min'] },
    { key: 'style', label: 'Style de vidéo', type: 'select', options: ['Motion design', 'Prise de vue réelle', 'Animation 2D/3D', 'Mixte'] },
    { key: 'voiceover', label: 'Voix off souhaitée', type: 'checkbox' },
    { key: 'music', label: 'Type de musique', type: 'select', options: ['Dynamique', 'Corporate', 'Émotionnelle', 'Pas de musique'] }
  ],
  logo: [
    { key: 'style_preference', label: 'Style souhaité', type: 'select', options: ['Moderne', 'Classique', 'Minimaliste', 'Illustration', 'Typographique'] },
    { key: 'colors', label: 'Couleurs souhaitées', type: 'text', placeholder: 'Ex: Bleu, vert, tons naturels...' },
    { key: 'variations', label: 'Déclinaisons', type: 'multi', options: ['Couleur', 'Noir et blanc', 'Favicon', 'Cartes de visite'] }
  ],
  ecommerce: [
    { key: 'products_count', label: 'Nombre de produits', type: 'select', options: ['< 50', '50-200', '200-1000', '> 1000'] },
    { key: 'payment_methods', label: 'Moyens de paiement', type: 'multi', options: ['Carte bancaire', 'PayPal', 'Paiement en plusieurs fois', 'Virement'] },
    { key: 'shipping', label: 'Livraison', type: 'multi', options: ['France métropolitaine', 'Europe', 'International', 'Click & Collect'] },
    { key: 'features', label: 'Fonctionnalités', type: 'multi', options: ['Comptes clients', 'Avis produits', 'Wishlist', 'Filtres avancés'] }
  ],
  marketing: [
    { key: 'channels', label: 'Canaux souhaités', type: 'multi', options: ['Facebook/Instagram', 'Google Ads', 'LinkedIn', 'TikTok', 'Emailing'] },
    { key: 'budget_ads', label: 'Budget publicitaire mensuel', type: 'select', options: ['< 500€', '500€ - 2000€', '2000€ - 5000€', '> 5000€'] },
    { key: 'objectives', label: 'Objectifs principaux', type: 'multi', options: ['Notoriété', 'Génération de leads', 'Ventes', 'Trafic site', 'Engagement'] }
  ],
  other: []
};

const PublicQuotePage = () => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    project_title: '',
    project_type: '',
    description: '',
    budget_preference: '',
    deadline_preference: '',
    inspiration_links: [''],
    brief_data: {}
  });

  // Fetch agency info for branding
  useEffect(() => {
    fetch(`${API_URL}/api/settings/agency-info`)
      .then(res => res.json())
      .then(data => setAgencyInfo(data))
      .catch(() => {});
  }, []);

  const selectedType = PROJECT_TYPES.find(t => t.value === formData.project_type);
  const briefFields = BRIEF_FIELDS[formData.project_type] || [];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateBriefField = (key, value) => {
    setFormData(prev => ({
      ...prev,
      brief_data: { ...prev.brief_data, [key]: value }
    }));
  };

  const toggleMultiOption = (key, option) => {
    const current = formData.brief_data[key] || [];
    const updated = current.includes(option)
      ? current.filter(o => o !== option)
      : [...current, option];
    updateBriefField(key, updated);
  };

  const addLink = () => {
    setFormData(prev => ({
      ...prev,
      inspiration_links: [...prev.inspiration_links, '']
    }));
  };

  const updateLink = (index, value) => {
    const newLinks = [...formData.inspiration_links];
    newLinks[index] = value;
    setFormData(prev => ({ ...prev, inspiration_links: newLinks }));
  };

  const removeLink = (index) => {
    setFormData(prev => ({
      ...prev,
      inspiration_links: prev.inspiration_links.filter((_, i) => i !== index)
    }));
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      return formData.name && formData.email && formData.project_type;
    }
    if (stepNum === 2) {
      return formData.project_title && formData.description;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep(1) || !validateStep(2)) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/public-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          inspiration_links: formData.inspiration_links.filter(l => l.trim()),
          source_url: window.location.href
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        toast.success('Demande envoyée avec succès !');
      } else {
        throw new Error(data.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="max-w-lg w-full text-center shadow-xl">
          <CardContent className="py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">Merci pour votre demande !</h2>
            <p className="text-slate-600 mb-6">
              Nous avons bien reçu votre brief et nous vous recontacterons dans les plus brefs délais pour discuter de votre projet.
            </p>
            <div className="p-4 bg-slate-50 rounded-lg text-left">
              <p className="text-sm text-slate-500 mb-2">Récapitulatif :</p>
              <p className="font-medium text-slate-800">{formData.project_title}</p>
              <p className="text-sm text-slate-600">{selectedType?.label}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {agencyInfo?.logo_url ? (
              <img src={agencyInfo.logo_url} alt={agencyInfo?.name} className="h-10 object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold">
                {agencyInfo?.name?.charAt(0) || 'A'}
              </div>
            )}
            <div>
              <h1 className="font-bold text-slate-800">{agencyInfo?.name || 'Agence'}</h1>
              <p className="text-xs text-slate-500">Demande de devis</p>
            </div>
          </div>
          <Badge variant="outline" className="text-cyan-600 border-cyan-200">
            Étape {step}/3
          </Badge>
        </div>
      </header>

      {/* Progress bar */}
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-slate-500">
          <span className={step >= 1 ? 'text-cyan-600 font-medium' : ''}>Vos coordonnées</span>
          <span className={step >= 2 ? 'text-cyan-600 font-medium' : ''}>Votre projet</span>
          <span className={step >= 3 ? 'text-cyan-600 font-medium' : ''}>Détails du brief</span>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Step 1: Contact info + Project type */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Parlons de votre projet</h2>
              <p className="text-slate-600">Commencez par nous donner vos coordonnées et le type de projet souhaité</p>
            </div>

            {/* Contact info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-cyan-500" />
                  Vos coordonnées
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nom complet *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="Jean Dupont"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="jean@exemple.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Société</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => updateField('company', e.target.value)}
                    placeholder="Nom de votre entreprise"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Project type selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-cyan-500" />
                  Type de projet *
                </CardTitle>
                <CardDescription>Sélectionnez le type qui correspond le mieux à votre besoin</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {PROJECT_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.project_type === type.value;
                    return (
                      <button
                        key={type.value}
                        onClick={() => updateField('project_type', type.value)}
                        className={cn(
                          "p-4 rounded-xl border-2 transition-all text-left",
                          isSelected
                            ? "border-cyan-500 bg-cyan-50"
                            : "border-slate-200 hover:border-slate-300"
                        )}
                      >
                        <Icon className={cn("w-6 h-6 mb-2", isSelected ? "text-cyan-600" : "text-slate-400")} />
                        <div className="font-medium text-sm text-slate-800">{type.label}</div>
                        <div className="text-xs text-slate-500 mt-1">{type.description}</div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex justify-end">
              <Button
                onClick={() => setStep(2)}
                disabled={!validateStep(1)}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
              >
                Continuer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Project details */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Décrivez votre projet</h2>
              <p className="text-slate-600">Plus vous êtes précis, plus nous pourrons vous proposer une solution adaptée</p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {selectedType && <selectedType.icon className="w-5 h-5 text-cyan-500" />}
                  Informations du projet
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Titre du projet *</Label>
                  <Input
                    value={formData.project_title}
                    onChange={(e) => updateField('project_title', e.target.value)}
                    placeholder="Ex: Refonte de notre site web corporate"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Description détaillée *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Décrivez votre projet, vos objectifs, votre cible..."
                    className="mt-1 min-h-[150px]"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Coins className="w-4 h-4 text-slate-400" />
                      Budget estimé
                    </Label>
                    <Select
                      value={formData.budget_preference}
                      onValueChange={(v) => updateField('budget_preference', v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Sélectionnez une fourchette" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      Délai souhaité
                    </Label>
                    <Select
                      value={formData.deadline_preference}
                      onValueChange={(v) => updateField('deadline_preference', v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Sélectionnez un délai" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEADLINE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Inspiration links */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <LinkIcon className="w-4 h-4 text-slate-400" />
                    Liens d'inspiration (optionnel)
                  </Label>
                  <div className="space-y-2">
                    {formData.inspiration_links.map((link, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={link}
                          onChange={(e) => updateLink(index, e.target.value)}
                          placeholder="https://exemple.com"
                          className="flex-1"
                        />
                        {formData.inspiration_links.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeLink(index)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addLink}
                      className="text-cyan-600"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter un lien
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!validateStep(2)}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
              >
                Continuer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Brief details */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Détails du brief</h2>
              <p className="text-slate-600">Quelques questions spécifiques pour mieux comprendre vos besoins</p>
            </div>

            {briefFields.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-cyan-500" />
                    Questions spécifiques - {selectedType?.label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {briefFields.map((field) => (
                    <div key={field.key}>
                      <Label className="mb-2 block">{field.label}</Label>
                      
                      {field.type === 'select' && (
                        <Select
                          value={formData.brief_data[field.key] || ''}
                          onValueChange={(v) => updateBriefField(field.key, v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez une option" />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options.map((opt) => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {field.type === 'text' && (
                        <Input
                          value={formData.brief_data[field.key] || ''}
                          onChange={(e) => updateBriefField(field.key, e.target.value)}
                          placeholder={field.placeholder}
                        />
                      )}
                      
                      {field.type === 'checkbox' && (
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            checked={formData.brief_data[field.key] || false}
                            onCheckedChange={(v) => updateBriefField(field.key, v)}
                          />
                          <span className="text-sm text-slate-600">Oui</span>
                        </div>
                      )}
                      
                      {field.type === 'multi' && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {field.options.map((opt) => {
                            const isSelected = (formData.brief_data[field.key] || []).includes(opt);
                            return (
                              <button
                                key={opt}
                                onClick={() => toggleMultiOption(field.key, opt)}
                                className={cn(
                                  "px-3 py-1.5 rounded-full text-sm border transition-all",
                                  isSelected
                                    ? "bg-cyan-500 text-white border-cyan-500"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-cyan-300"
                                )}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">
                    Pas de questions spécifiques pour ce type de projet.
                    <br />
                    Vous pouvez soumettre votre demande !
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Summary */}
            <Card className="border-cyan-200 bg-cyan-50/50">
              <CardHeader>
                <CardTitle className="text-lg">Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Contact</span>
                  <span className="font-medium">{formData.name} ({formData.email})</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Projet</span>
                  <span className="font-medium">{formData.project_title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Type</span>
                  <Badge variant="outline">{selectedType?.label}</Badge>
                </div>
                {formData.budget_preference && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Budget</span>
                    <span className="font-medium">
                      {BUDGET_OPTIONS.find(b => b.value === formData.budget_preference)?.label}
                    </span>
                  </div>
                )}
                {formData.deadline_preference && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Délai</span>
                    <span className="font-medium">
                      {DEADLINE_OPTIONS.find(d => d.value === formData.deadline_preference)?.label}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer ma demande
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-auto py-6 text-center text-sm text-slate-500">
        <p>{agencyInfo?.name || 'Agence'} © {new Date().getFullYear()}</p>
        {agencyInfo?.phone && <p className="mt-1">Tél: {agencyInfo.phone}</p>}
      </footer>
    </div>
  );
};

export default PublicQuotePage;
